/**
 * melody — 노래 따라부르기의 **레퍼런스**(부를 멜로디 + 들려줄 음원)를 읽는다.
 *
 * 포맷은 admin 훈련 차트 그대로다(`{ songId, melody: [{ id, midi, startMs, durMs }] }`) —
 * 변환 레이어를 두지 않는다. 같은 파일을 두 앱이 각자 해석하기 시작하면 "정답 멜로디"가
 * 둘이 된다. 에셋은 web `public/diagnosis/`로 **복사**해 뒀다: admin은 별개 앱·별개
 * 배포라 프로덕션에서 그 경로를 부르면 404다.
 *
 * 지금 실린 곡은 팀이 준 **18.05초**짜리 마스터 두 벌이다 — 녹음 중에는 반주(MR)를 틀고,
 * 부르기 전 미리듣기에는 가이드 보컬이 실린 쪽을 튼다(`guideAudioUrl`). 차트는 두 마스터의
 * 차(GUIDE−MR = 가이드 보컬 스템)에서 뽑은 **자리표시자**다 — 음 23개, 0.72~16.60초 구간.
 * 사람이 검수한 차트가 아니라는 사실은 JSON의 `note` 필드에도 적어 뒀다. admin 훈련 차트
 * 에디터로 다듬으면 `SONG_REFERENCE_URL` 하나만 바꾼다.
 */

/**
 * 레퍼런스 교체 seam — 검수된 차트가 오면 **이 상수 하나**만 바꾼다.
 *
 * 차트에는 **겹치는 음이 없어야 한다**: 사람은 한 번에 한 음만 내므로 같은 시각에 두 음이
 * 실려 있으면 음원대로 완벽히 부른 사람도 그중 하나는 반드시 틀리고, 구간 분석이 없는
 * 경향을 지어낸다(이전 곡 `song-001`에서 손편집 음 4개를 뺀 이유 — 실측 94점 + "고음
 * 구간이 낮았다"는 거짓말). `melody.spec`이 배포되는 파일을 직접 읽어 그 조건을 잡는다.
 */
export const SONG_REFERENCE_URL = '/diagnosis/song-002.json';

export interface MelodyNote {
  id: string;
  /** MIDI 노트 번호(A4 = 69). */
  midi: number;
  /** 음원 시작(t=0) 기준 시작 시각(ms). */
  startMs: number;
  durMs: number;
}

/**
 * 마지막 **차트 음** 뒤에 붙이는 여유.
 *
 * 화면 오른쪽 끝에 딱 붙으면 끝난 줄 모른다는 것이 원래 이유였지만, 이 값은 그보다 센
 * 일을 한다: 녹음이 끝나면 레퍼런스 재생도 같이 멈추므로(`SongReferenceAudio`),
 * **차트 끝 + 이 여유가 음원 길이를 못 덮으면 음원이 중간에 끊긴다.** 지금 차트는 5.41초에
 * 끝나고 음원은 6.905초라 1초로는 0.5초가 잘렸다 — 사용자에게는 "노래가 끊긴다"로 보인다.
 * 그 관계는 `melody.spec.ts`가 배포된 wav 길이를 직접 읽어서 잡고 있다.
 */
const TAIL_MS = 2000;

/**
 * 이 테이크가 덮는 시간(초). 비어 있으면 null.
 *
 * **이 값 하나가 노래 테이크의 길이다** — 녹음 제한시간이자 곡선 가로축의 폭
 * (`recordTests.takeSpanSec`). 둘이 다른 값을 쓰면 축이 안 덮는 구간이 생기고 그 구간의
 * 점은 전부 오른쪽 끝에 쌓인다(실측: 6.41초 축에 20초 제한 → 13.6초가 x=100에 뭉쳤다,
 * `.claude/20260805/DEBUG-15-49-40.md`).
 *
 * **음원 길이를 같이 받는다.** 차트 끝 + 여유만 쓰면 노래가 중간에 끊긴다: 지금 곡은
 * 가이드 보컬이 14.6초에 끝나고 음원은 18.05초라(뒤는 반주 아웃트로) 차트만 따르면
 * 사용자에게는 "노래가 끊긴다"로 보인다. 예전에는 이 관계를 `melody.spec`이 배포 wav를
 * 직접 읽어 사후에 잡았는데, 그건 에셋을 바꿀 때마다 터지는 경보이지 계약이 아니었다 —
 * 이제 음원 길이가 차트에 실려 오고 여기서 함께 계산한다.
 */
export const melodySpanSec = (
  melody: MelodyNote[],
  audioDurationMs?: number,
): number | null =>
  melody.length === 0
    ? null
    : Math.max(
        Math.max(...melody.map((n) => n.startMs + n.durMs)) + TAIL_MS,
        audioDurationMs ?? 0,
      ) / 1000;

export interface SongReference {
  songId: string;
  /**
   * **녹음 중에** 흘려 주는 음원 — 반주(MR)다. 차트가 아니라 여기 실린 값이 정본이다
   * (web 경로로 고쳐 담았다).
   *
   * 가이드 보컬이 실린 음원을 여기 걸면 안 된다: 스피커로 나가는 가이드를 마이크가
   * 같이 듣고 f0로 잡아 "부른 것"으로 채점된다(songScoring 한계 주석).
   */
  audioUrl: string;
  /**
   * **부르기 전에** 들려주는 음원 — 같은 곡의 가이드 보컬 버전. 미리듣기는 무엇을 부를지
   * 알려 주는 자리라 멜로디가 들려야 하고, 녹음 중에는 그 가이드가 채점을 오염시킨다.
   * 그래서 둘이 다른 파일이다.
   *
   * **선택 필드다.** 없으면 `audioUrl`을 쓴다 — 가이드 버전이 따로 안 온 곡은 미리듣기도
   * 반주만 들려준다. 지금 실린 곡에는 둘 다 있다.
   */
  guideAudioUrl?: string;
  /**
   * `audioUrl` 음원의 길이(ms). 테이크가 음원을 끝까지 덮게 하는 값이다
   * (`melodySpanSec`) — 없으면 차트 끝 + 여유만 쓴다.
   */
  durationMs?: number;
  /** 시작 시각 오름차순. */
  melody: MelodyNote[];
}

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const toNote = (v: unknown): MelodyNote | null => {
  const n = v as MelodyNote | undefined;
  if (
    n == null ||
    typeof n !== 'object' ||
    typeof n.id !== 'string' ||
    !isFiniteNumber(n.midi) ||
    !isFiniteNumber(n.startMs) ||
    !isFiniteNumber(n.durMs) ||
    n.durMs <= 0
  ) {
    return null;
  }
  return { id: n.id, midi: n.midi, startMs: n.startMs, durMs: n.durMs };
};

/**
 * 파싱 + 검증. 하나라도 모양이 틀리면 **통째로 null**이다 — 음 몇 개를 조용히 버리면
 * 정확도의 분모가 달라지고, 화면은 그걸 "그 음을 안 불렀다"로 읽는다.
 *
 * 시작 시각으로 정렬해 낸다: 차트는 편집 순서대로 저장돼 뒤에 얹은 음이 뒤에 붙어 있다.
 */
export const parseSongReference = (raw: unknown): SongReference | null => {
  const doc = raw as SongReference | undefined;
  if (
    doc == null ||
    typeof doc !== 'object' ||
    typeof doc.songId !== 'string' ||
    typeof doc.audioUrl !== 'string' ||
    (doc.guideAudioUrl != null && typeof doc.guideAudioUrl !== 'string') ||
    (doc.durationMs != null && !isFiniteNumber(doc.durationMs)) ||
    !Array.isArray(doc.melody) ||
    doc.melody.length === 0
  ) {
    return null;
  }
  const notes = doc.melody.map(toNote);
  if (notes.some((n) => n == null)) {
    return null;
  }
  return {
    songId: doc.songId,
    audioUrl: doc.audioUrl,
    ...(doc.guideAudioUrl != null ? { guideAudioUrl: doc.guideAudioUrl } : {}),
    ...(doc.durationMs != null ? { durationMs: doc.durationMs } : {}),
    melody: (notes as MelodyNote[])
      .slice()
      .sort((a, b) => a.startMs - b.startMs),
  };
};

/**
 * 레퍼런스를 받아온다. **음원까지 실제로 받는다**(objectURL): 차트만 읽고 시작하면
 * 재생 실패를 녹음이 이미 돌아간 뒤에야 알게 되고, 그 테이크는 채점할 수 없다.
 *
 * 실패는 던진다 — 화면이 재시도/건너뛰기를 내는 자리에서 잡는다.
 */
const fetchObjectUrl = async (url: string, what: string): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`${what} ${res.status}`);
  }
  return URL.createObjectURL(await res.blob());
};

export const loadSongReference = async (
  url: string = SONG_REFERENCE_URL,
): Promise<
  SongReference & { audioObjectUrl: string; guideAudioObjectUrl: string }
> => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`song reference ${res.status}`);
  }
  const reference = parseSongReference(await res.json());
  if (!reference) {
    throw new Error('song reference malformed');
  }
  const audioObjectUrl = await fetchObjectUrl(reference.audioUrl, 'song audio');
  // 가이드가 따로 없으면 반주 하나를 두 자리에 쓴다 — 같은 objectURL이라 blob도 하나다.
  // 여기서 두 번 받으면 되돌릴 URL이 둘이 되고, 화면은 하나만 revoke한다.
  const guideAudioObjectUrl =
    reference.guideAudioUrl == null
      ? audioObjectUrl
      : await fetchObjectUrl(reference.guideAudioUrl, 'song guide audio');
  return { ...reference, audioObjectUrl, guideAudioObjectUrl };
};
